import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Trophy, Calendar, Clock, MapPin } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { cn, asArray } from "@/lib/utils";

type AvailabilityStatus = "yes" | "no" | "maybe";

interface GameKid {
  id: number;
  firstName: string;
  lastName: string;
}

interface GameMyResponse {
  kidId: number;
  status: AvailabilityStatus;
}

interface Game {
  id: number;
  batchId: number;
  opponent: string | null;
  startTime: string;
  venueName: string;
  venueAddress: string | null;
  status: "scheduled" | "cancelled";
  kids: GameKid[];
  myResponses: GameMyResponse[];
}

// Deliberately its own page, not folded into Schedule.tsx (practice RSVP) —
// a game is a one-off fixture against an opponent, not a recurring
// practice slot, and per Maddy's requirement these need to stay visibly
// distinct to a parent, not blend together. Same Going/Not Going/Unsure
// vocabulary and card layout as Schedule.tsx deliberately reused, though —
// this session's own history has an RSVP-wording-mismatch bug on record,
// and a parent shouldn't have to learn two different vocabularies for the
// same yes/no/maybe concept.
export default function ParentAvailability() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  // Same rare-twins case as Schedule.tsx: which kid a given game card's
  // RSVP buttons act on, when more than one of the parent's kids share
  // the game's batch.
  const [selectedKidByGame, setSelectedKidByGame] = useState<Record<number, number>>({});

  // staleTime: 0 — same reasoning as Schedule.tsx: games and responses can
  // change from the coach side between visits, and the app-wide default
  // (queryClient.ts) is staleTime: Infinity.
  const { data, isLoading, error } = useQuery({
    queryKey: ["/api/games/mine"],
    queryFn: async () => {
      const res = await fetch("/api/games/mine", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch games");
      return res.json();
    },
    staleTime: 0,
  });

  const games: Game[] = asArray<Game>(data?.data);

  // Fetch parent's kids (used only to decide whether to show the
  // "RSVP for {name}" attribution label — hidden when the parent only has
  // one kid, since it'd be redundant). Same pattern as Schedule.tsx.
  const { data: kidsData } = useQuery({
    queryKey: ["/api/parent/kids"],
    queryFn: async () => {
      const res = await fetch("/api/parent/kids", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch kids");
      return res.json();
    },
    staleTime: 0,
  });

  const kids = asArray<{ id: number }>(kidsData?.data);
  const parentHasMultipleKids = kids.length > 1;

  const responseMap = new Map<string, AvailabilityStatus>();
  for (const game of games) {
    for (const r of game.myResponses ?? []) {
      responseMap.set(`${game.id}-${r.kidId}`, r.status);
    }
  }

  const updateAvailabilityMutation = useMutation({
    mutationFn: async ({ gameId, kidId, status }: { gameId: number; kidId: number; status: AvailabilityStatus }) => {
      const res = await fetch(`/api/games/${gameId}/availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ kidId, status }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || errorData.error || "Failed to update availability");
      }
      return res.json();
    },
    onMutate: async ({ gameId, kidId, status }) => {
      const key = `${gameId}-${kidId}`;
      setUpdatingKey(key);

      await queryClient.cancelQueries({ queryKey: ["/api/games/mine"] });
      const previousData = queryClient.getQueryData(["/api/games/mine"]);

      queryClient.setQueryData(["/api/games/mine"], (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((g: Game) => {
            if (g.id !== gameId) return g;
            const existing = (g.myResponses ?? []).find((r) => r.kidId === kidId);
            const myResponses = existing
              ? g.myResponses.map((r) => (r.kidId === kidId ? { ...r, status } : r))
              : [...(g.myResponses ?? []), { kidId, status }];
            return { ...g, myResponses };
          }),
        };
      });

      return { previousData };
    },
    onError: (err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(["/api/games/mine"], context.previousData);
      }
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to update availability",
        variant: "destructive",
      });
      setUpdatingKey(null);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Availability updated successfully",
      });
      setUpdatingKey(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/games/mine"] });
    },
  });

  const handleUpdate = (gameId: number, kidId: number, status: AvailabilityStatus) => {
    updateAvailabilityMutation.mutate({ gameId, kidId, status });
  };

  const getAvailabilityBadge = (status: AvailabilityStatus, gameId: number) => {
    switch (status) {
      case "yes":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100" data-testid={`game-rsvp-badge-going-${gameId}`}>Going</Badge>;
      case "no":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100" data-testid={`game-rsvp-badge-not-going-${gameId}`}>Not Going</Badge>;
      case "maybe":
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100" data-testid={`game-rsvp-badge-unsure-${gameId}`}>Unsure</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl" data-testid="parent-availability-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Game Availability</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Let your coach know which upcoming games your kids can make
        </p>
      </div>

      <Card data-testid="upcoming-games-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Games
          </CardTitle>
          <CardDescription>Upcoming games for your kids' teams</CardDescription>
        </CardHeader>
        <CardContent data-testid="upcoming-games-list">
          {isLoading ? (
            <div className="text-center py-8" data-testid="loading-games">Loading games...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-600" data-testid="error-games">
              Error loading games. Please try again.
            </div>
          ) : games.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-games">
              No upcoming games scheduled
            </div>
          ) : (
            <div className="space-y-4">
              {games.map((game) => {
                const gameKids = game.kids ?? [];
                const selectedKidId = selectedKidByGame[game.id] ?? gameKids[0]?.id ?? null;
                const kid = gameKids.find((k) => k.id === selectedKidId) ?? null;
                const responseKey = kid ? `${game.id}-${kid.id}` : null;
                const currentStatus = responseKey ? responseMap.get(responseKey) || "maybe" : "maybe";
                const isUpdating = responseKey === updatingKey;

                return (
                  <div
                    key={game.id}
                    className="border rounded-lg p-3 sm:p-4 space-y-3"
                    data-testid={`game-card-${game.id}`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-base sm:text-lg break-words flex-1">
                          {game.opponent ? `vs ${game.opponent}` : "Game"}
                        </div>
                        {getAvailabilityBadge(currentStatus, game.id)}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
                      <div className="flex items-center gap-1 min-w-0">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{format(new Date(game.startTime), "MMM dd, yyyy")}</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-0">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{format(new Date(game.startTime), "h:mm a")}</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-0">
                        <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{game.venueName}</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t space-y-2">
                      {gameKids.length > 1 && (
                        <div className="flex flex-wrap gap-1" data-testid={`kid-selector-${game.id}`}>
                          {gameKids.map((k) => (
                            <button
                              key={k.id}
                              type="button"
                              onClick={() => setSelectedKidByGame((prev) => ({ ...prev, [game.id]: k.id }))}
                              data-testid={`kid-pill-${game.id}-${k.id}`}
                              className={cn(
                                "px-2 py-1 rounded-full text-xs font-medium border transition-colors",
                                k.id === selectedKidId
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-muted-foreground border-input hover:bg-muted"
                              )}
                            >
                              {k.firstName}
                            </button>
                          ))}
                        </div>
                      )}

                      {kid && gameKids.length === 1 && parentHasMultipleKids && (
                        <p className="text-xs text-muted-foreground">Responding for {kid.firstName}</p>
                      )}

                      {kid && (
                        <div className="flex flex-col sm:flex-row gap-2" data-testid={`game-rsvp-actions-${game.id}`}>
                          <Button
                            size="sm"
                            variant={currentStatus === "yes" ? "default" : "outline"}
                            onClick={() => handleUpdate(game.id, kid.id, "yes")}
                            disabled={isUpdating}
                            data-testid={`btn-game-rsvp-going-${game.id}`}
                            className="flex-1 sm:flex-none"
                          >
                            Going
                          </Button>
                          <Button
                            size="sm"
                            variant={currentStatus === "no" ? "default" : "outline"}
                            onClick={() => handleUpdate(game.id, kid.id, "no")}
                            disabled={isUpdating}
                            data-testid={`btn-game-rsvp-not-going-${game.id}`}
                            className="flex-1 sm:flex-none"
                          >
                            Not Going
                          </Button>
                          <Button
                            size="sm"
                            variant={currentStatus === "maybe" ? "default" : "outline"}
                            onClick={() => handleUpdate(game.id, kid.id, "maybe")}
                            disabled={isUpdating}
                            data-testid={`btn-game-rsvp-unsure-${game.id}`}
                            className="flex-1 sm:flex-none"
                          >
                            Unsure
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
